<template>
	<v-skeleton-loader
		v-if="loading"
		type="paragraph, button"
	/>
	<div
		v-else
	>
		<v-row>
			<v-col>
				<v-card>
					<v-card-title class="d-flex align-center">
						<v-icon
							icon="$user"
							start
						/>
						<translate>Personal Data</translate>
					</v-card-title>
					<v-card-text>
						<v-row
							v-if="userId"
							v-can="'user.showid'"
						>
							<v-col
								cols="12"
								lg="6"
							>
								<v-text-field
									v-model="user.id"
									:label="$gettext('Customer Number')"
									hide-details
									disabled
									variant="outlined"
								/>
							</v-col>
							<v-col
								cols="12"
								xl="6"
							>
								<copy-to-clipboard-button
									:copy-data="user.id"
									variant="outlined"
								>
									<translate>Copy Customer Number</translate>
								</copy-to-clipboard-button>
							</v-col>
						</v-row>
						<v-row>
							<v-col
								cols="12"
								lg="6"
							>
								<v-form ref="email">
									<v-text-field
										v-model="user.email"
										:label="$gettext('Email Address')"
										hide-details
										:rules="[v => emailRegex.test(v) || $gettext('Enter an email')]"
										required
										variant="outlined"
									/>
								</v-form>
							</v-col>
							<v-col
								cols="12"
								xl="6"
							>
								<v-btn
									v-if="userId"
									variant="outlined"
									@click="sendActivationMail"
								>
									<translate>Send Activation Email</translate>
								</v-btn>
							</v-col>
						</v-row>
						<v-row class="py-0 my-0">
							<v-col
								cols="12"
								lg="6"
							>
								<language-select
									v-model="user.languageId"
									variant="outlined"
								/>
								<domain-select
									v-model="user.domainId"
									:disabled="!isAdmin && userId"
									variant="outlined"
								/>
							</v-col>
							<v-col
								cols="12"
								md="6"
							>
								<v-radio-group
									v-model="user.gender"
									:label="$gettext('Gender')"
									mandatory
									class="mt-0"
									hide-details
								>
									<v-radio
										color="primary"
										:label="$gettext('Male')"
										value="M"
									/>
									<v-radio
										color="primary"
										:label="$gettext('Female')"
										value="F"
									/>
								</v-radio-group>
							</v-col>
						</v-row>
						<v-row class="my-0">
							<v-col
								cols="6"
								class="py-0"
							>
								<date-text-field
									v-model="user.dateBirth"
									variant="outlined"
								/>
							</v-col>
						</v-row>
						<v-row class="my-0">
							<v-col
								cols="5"
								class="py-0"
							>
								<v-text-field
									v-model="user.nameInitials"
									:label="$gettext('Initials (format : A.B.)')"
									variant="outlined"
								/>
							</v-col>
							<v-col
								cols="7"
								class="py-0"
							>
								<v-text-field
									v-model="user.nameFirst"
									:label="$gettext('Nickname')"
									variant="outlined"
								/>
							</v-col>
						</v-row>
						<v-row />
						<v-row class="my-0">
							<v-col
								cols="4"
								class="py-0"
							>
								<v-text-field
									v-model="user.nameInfix"
									:label="$gettext('Infix')"
									variant="outlined"
								/>
							</v-col>
							<v-col
								cols="8"
								class="py-0"
							>
								<v-text-field
									v-model="user.nameLast"
									:label="$gettext('Last Name')"
									variant="outlined"
								/>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn
							v-can="'user.update'"
							color="primary"
							@click="submitData"
						>
							<translate>Save</translate>
						</v-btn>
					</v-card-actions>
					<warning-popup v-if="displayWarning">
						<translate>invalidEmail</translate>
					</warning-popup>
				</v-card>
			</v-col>
		</v-row>
		<v-row>
			<v-col>
				<v-card>
					<v-card-title class="d-flex align-center">
						<v-icon
							icon="$building"
							start
						/>
						<translate>For Businesses</translate>
					</v-card-title>
					<v-card-text>
						<v-row class="my-0">
							<v-col
								class="py-0"
							>
								<v-checkbox
									v-model="user.isBusiness"
									color="primary"
									:label="$gettext('For Businesses')"
									hide-details
								/>
							</v-col>
						</v-row>
						<v-row class="my-0">
							<v-col
								class="py-0"
							>
								<v-text-field
									v-model="user.businessName"
									:label="$gettext('Company Name')"
									variant="outlined"
								/>
							</v-col>
						</v-row>
						<v-row class="my-0">
							<v-col
								class="py-0"
							>
								<v-text-field
									v-model="user.businessKvk"
									:label="$gettext('Chamber of Commerce Number')"
									variant="outlined"
								/>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn
							v-can="'user.update'"
							color="primary"
							@click="submitData"
						>
							<translate>Save</translate>
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import {
	axios,
} from '../../services/axios-service';
import {
	inform, displayError,
} from 'laravel-front-library';
import {
	debounce,
} from 'lodash';

export default {
	name: 'UserPersonalDataEdit',
	props: {
		userId: {
			required: false,
			type: [Number, null, ],
			default: null
		},
		isAdmin: {
			required: false,
			type: [Boolean, ],
			default: false
		}
	},
	data () {
		return {
			user: {
				'id': '',
				'email': '',
				'gender': 'M',
				'domainId': null,
				'languageId': null,
				'dateBirth': '',
				'nameInitials': '',
				'nameInfix': '',
				'nameLast': '',
				'isBusiness': false,
				'businessName': '',
				'businessKvk': '',
			},
			loading: true,
			emailRegex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
			displayWarning: false,
		};
	},

	created () {
		if (this.userId) {
			this.getUserData();
		} else {
			this.loading = false;
		}
	},
	methods: {
		/**
		 * Gets the user data of currently logged-in user
		 */
		getUserData () {
			axios.get(`/api/v0/users?id=${this.userId}`).then(({
				data
			}) => {
				this.user = data[0];
				this.loading = false;
				this.displayWarning = !this.emailRegex.test(this.user.email);
			}).catch((error) => {
				displayError(error);
				window.captureException(error);
			});
		},

		/**
		 * Send activation mail to the currently written email
		 */
		sendActivationMail () {
			const formData = new FormData();

			formData.append('email_address', this.user.email);
			formData.append('csrf_token', this.$csrfToken);

			axios.post('/welcome/doActivation', formData).then(() => {
				inform('Activation mail sent');
				debounce(() => {
					window.location.reload();
				}, 1000)();
			}).catch((
				error
			) => {
				displayError(error);
				window.captureException(error);
			});
		},

		/**
		 * Submits data for either a new user or an existing user
		 */
		submitData () {
			const data = {
				'email': this.user.email,
				'gender': this.user.gender,
				'domainId': this.user.domainId,
				'languageId': this.user.languageId,
				'dateBirth': this.user.dateBirth,
				'nameInitials': this.user.nameInitials,
				'nameFirst': this.user.nameFirst,
				'nameInfix': this.user.nameInfix,
				'nameLast': this.user.nameLast,
				'isBusiness': this.user.isBusiness,
				'businessName': this.user.businessName,
				'businessKvk': this.user.businessKvk
			};

			if (this.userId) {
				this.putPersonalInfo(data);
			} else {
				this.postPersonalInfo(data);
			}
		},

		/**
		 * @param {object} requestData
		 */
		postPersonalInfo (requestData) {
			axios.post('/api/v0/users', requestData).then(({
				data
			}) => {
				inform('User successfully created');
				window.location.href = `/bo/users/view?id=${data}`;
			}).catch((
				error
			) => {
				if (error.response.status === 303) {
					displayError(this.$gettext('A user already exists with that e-mail address, but with different data.'));
					debounce(() => {
						window.location.href = `/bo/users/view?id=${error.response.data}`;
					}, 1000)();
				} else {
					displayError(this.$gettext('Unable to save user, please check your data and try again.'));
					window.captureException(error);
				}
			});
		},

		/**
		 * @param {object} requestData
		 */
		putPersonalInfo (requestData) {
			axios.put(`/api/v0/users/${this.userId}`, requestData).then(
				() => {
					inform('User successfully saved');
					window.location.href = `/bo/users/view?id=${this.userId}`;

				}).catch((
				error
			) => {
				if (error.response.status === 303) {
					displayError(this.$gettext('A user already exists with that e-mail address, but with different data.'));
					debounce(() => {
						window.location.href = `/bo/users/view?id=${error.response.data}`;
					}, 1000)();
				} else {
					displayError('Something went wrong');
					window.captureException(error);
				}
			});
		},
	}
};
</script>
